import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import { type AdGroupActivityDataType, useAdGroupActivityFitScore } from '@/api/ad';
import type { DataTableColumnDef } from '@/components/DataTable';
import {
  FitScoreLegend,
  FitScoreMetricCell,
  FitScoreTable,
  FitScoreTitleCell,
} from '@/components/FitScoreTable';
import { usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import { SupportLink } from '@/components/typography';
import { numberFormat } from '@/helper/numberFormatter';
import { ROUTES } from '@/router';

type Props = {
  adGroupIds: string[];
};

const PaidSearchAdGroupFitScoringTable = ({ adGroupIds }: Props) => {
  const {
    pageFilters: { startDate, endDate, audience },
  } = usePageFilterContext();
  const { fitScoreData, isLoading, error } = useAdGroupActivityFitScore(
    adGroupIds,
    startDate,
    endDate,
    audience?.id,
  );

  const columns = useMemo<DataTableColumnDef<AdGroupActivityDataType>[]>(
    () => [
      {
        id: 'name',
        header: t`Ad Group`,
        size: 300,
        minSize: 300,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreTitleCell
              title={field.adGroup.name}
              subtitle={
                <Trans>
                  {field.adGroup.campaign.vendor.name} -{' '}
                  {field.adGroup.campaign.vendor.channel?.name}
                </Trans>
              }
            />
          );
        },
      },
      {
        id: 'allVisits',
        accessorKey: 'allVisits',
        header: t`Visits`,
        minSize: 173,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.allVisitsBest}
              isWorst={field.allVisitsWorst}
              label={<Trans>Visits</Trans>}
              value={numberFormat(field.allVisits)}
              error={error}
              isLoading={false}
            />
          );
        },
      },
      {
        id: 'engagedCompanies',
        accessorKey: 'engagedCompanies',
        header: t`Engaged Companies`,
        minSize: 173,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.engagedCompaniesBest}
              isWorst={field.engagedCompaniesWorst}
              label={<Trans>Engaged Companies</Trans>}
              value={numberFormat(field.percentOfEngagedCompanies, {
                isPercent: true,
                precision: 1,
              })}
              caption={numberFormat(field.engagedCompanies)}
              captionLink={ROUTES.webTrafficActivityWithParams({
                'w.audienceIds': audience?.isTAM ? undefined : audience?.id,
                'w.inTam': audience?.isTAM ? 'true' : undefined,
                'w.adGroupIds': field.adGroup.id,
                'w.metrics.visits': '[1,]',
              })}
              error={error}
              isLoading={false}
            />
          );
        },
      },
      {
        id: 'spendPerVisit',
        accessorKey: 'spendPerTargetVisit',
        header: t`$/Visit`,
        minSize: 173,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.spendPerVisitBest}
              isWorst={field.spendPerVisitWorst}
              label={<Trans>$/Visit</Trans>}
              value={numberFormat(field.spendPerTargetVisit, { isCurrency: true, precision: 2 })}
              error={error}
              isLoading={false}
            />
          );
        },
      },
      {
        id: 'pipelineInfluenced',
        accessorKey: 'pipelineInfluencedPerSpend',
        header: t`$/Pipeline Influenced`,
        minSize: 173,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.pipelineInfluencedBest}
              isWorst={field.pipelineInfluencedWorst}
              label={<Trans>$/Pipeline Influenced</Trans>}
              value={numberFormat(field.pipelineInfluencedPerSpend, {
                isMultiplier: true,
                precision: 1,
              })}
              error={error}
              isLoading={false}
            />
          );
        },
      },
      {
        id: 'opportunityInfluenced',
        accessorKey: 'percentOpportunitiesInfluenced',
        header: t`% Opps Influenced`,
        minSize: 173,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.opportunityInfluencedBest}
              isWorst={field.opportunityInfluencedWorst}
              label={<Trans>% Opps Influenced</Trans>}
              value={numberFormat(field.percentOpportunitiesInfluenced, {
                isPercent: true,
                precision: 0,
              })}
              caption={numberFormat(field.opportunitiesInfluenced)}
              error={error}
              isLoading={false}
            />
          );
        },
      },
      {
        id: 'fitScore',
        accessorKey: 'fitScore',
        header: t`Fit Score`,
        minSize: 173,
        flex: 1,
        enableSorting: true,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.fitScoreBest}
              isWorst={field.fitScoreWorst}
              label={<Trans>Fit Score</Trans>}
              value={numberFormat(field.fitScore, { precision: 1 })}
              error={error}
              isLoading={false}
            />
          );
        },
      },
    ],
    [audience?.id, audience?.isTAM, error],
  );

  return (
    <Panel
      title={<Trans>Fit Score</Trans>}
      subtitle={audience?.isTAM ? t`TAM` : (audience?.name ?? t`Audience`)}
      infoPopover={{
        title: t`Fit Score`,
        body: (
          <Trans>
            The Ad Group Fit Score is an analysis of 5 major metrics used to calculate ad group
            performance from 1-100.{' '}
            <SupportLink variant="body2" color="static-green" path="FIT_SCORE">
              Learn more
            </SupportLink>
          </Trans>
        ),
      }}
      noPadding
    >
      <FitScoreLegend />
      <FitScoreTable<AdGroupActivityDataType>
        columns={columns}
        dataSource={fitScoreData}
        emptyMessage={t`Select ad groups in the table above and click the Score button to see your fit scores.`}
        loading={isLoading}
        error={error}
        rowKey={(record) => record.adGroup.id}
        tableClassName="min-w-325"
        initialColumnPinning={{ start: ['name'], end: ['fitScore'] }}
        defaultSorting={[{ id: 'fitScore', desc: true }]}
      />
    </Panel>
  );
};

export default PaidSearchAdGroupFitScoringTable;
